import { FuelProduct } from "@1games-ru/region-shared/dist/backend/requests/gas-station";
import { GasStationBrand } from "@1games-ru/region-shared/dist/enums/estate/gas-station";
import { PaymentType } from "@1games-ru/region-shared/dist/enums/payment/payment";
import {
  VehicleChargeGrade,
  VehicleFuelGrade,
  VehicleFuelType,
} from "@1games-ru/region-shared/dist/enums/vehicle/vehicle";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "@/core/store";
import { IS_DEV } from "@/domains/main/constants/other.ts";
import { fakeCar, fakeStation } from "@/domains/gasStation/helpers/fakeData";

export interface GasStationState {
  station: {
    id: number;
    name: string;
    address: {
      streetName: string;
      index: number;
    };
    fuelTypes: {
      [VehicleFuelType.Gas]: {
        [VehicleFuelGrade.Standard]: FuelProduct;
        [VehicleFuelGrade.Good]: FuelProduct;
        [VehicleFuelGrade.Excellent]: FuelProduct;
      } | null;
      [VehicleFuelType.Diesel]: FuelProduct | null;
      [VehicleFuelType.Electric]: {
        [VehicleChargeGrade.Standard]: FuelProduct;
        [VehicleChargeGrade.Fast]: FuelProduct;
      } | null;
      [VehicleFuelType.GasAvia]: FuelProduct | null;
    };
    brand: GasStationBrand;
  } | null;

  vehicle: {
    id: number;
    fuelType: VehicleFuelType;
    fuelAmount: number;
    fuelMaxAmount: number;

    brand: string;
    model: string;

    brandImage: string;
    modelImage: string;
  } | null;

  refuelingTime: Date | null;
  newVehicleFuelAmount: number | null;

  selectedFuelType: VehicleFuelType | null;
  selectedFuelGrade: VehicleFuelGrade | VehicleChargeGrade | null;
  selectedFuelAmount: number;
  selectedProductId: number;
  selectedPaymentType: PaymentType | null;
}

const initialState: GasStationState = {
  selectedFuelType: IS_DEV ? VehicleFuelType.Gas : null,
  selectedFuelGrade: IS_DEV ? VehicleFuelGrade.Standard : null,

  selectedFuelAmount: 0,
  selectedProductId: 0,

  newVehicleFuelAmount: null,

  selectedPaymentType: PaymentType.Card,

  station: IS_DEV ? fakeStation : null,
  vehicle: IS_DEV ? fakeCar : null,
  refuelingTime: null,
};

const gasStationSlice = createSlice({
  name: "gasStationSlice",
  initialState,
  reducers: {
    setStation: (state, action: PayloadAction<GasStationState["station"] | undefined>) => {
      state.station = action.payload || null;
    },
    setVehicle: (state, action: PayloadAction<GasStationState["vehicle"] | undefined>) => {
      state.vehicle = action.payload || null;
    },
    setSelectedFuelType: (state, action: PayloadAction<VehicleFuelType | null>) => {
      state.selectedFuelType = action.payload || null;
    },
    setSelectedFuelGrade: (
      state,
      action: PayloadAction<VehicleFuelGrade | VehicleChargeGrade | null>,
    ) => {
      state.selectedFuelGrade = action.payload || null;
    },
    setSelectedFuelAmount: (state, action: PayloadAction<number>) => {
      state.selectedFuelAmount = action.payload;
    },
    setSelectedProductId: (state, action: PayloadAction<number>) => {
      state.selectedProductId = action.payload;
    },
    setSelectedPaymentType: (state, action: PayloadAction<PaymentType>) => {
      state.selectedPaymentType = action.payload;
    },
    setRefuelingTime: (state, action: PayloadAction<Date | null>) => {
      state.refuelingTime = action.payload;
    },
    setNewVehicleFuelAmount: (state, action: PayloadAction<number | null>) => {
      state.newVehicleFuelAmount = action.payload;
    },
    setVehicleFuelAmount: (state, action: PayloadAction<number | null>) => {
      state.vehicle.fuelAmount = action.payload;
    },
    incrementVehicleFuelAmount: (state, action: PayloadAction<number>) => {
      if (state.vehicle) {
        state.vehicle.fuelAmount += action.payload;
        if (state.vehicle.fuelAmount > state.vehicle.fuelMaxAmount) {
          state.vehicle.fuelAmount = state.vehicle.fuelMaxAmount;
        }
      }
    },
    decrementNewVehicleFuelAmount: (state, action: PayloadAction<number>) => {
      if (state.newVehicleFuelAmount !== null) {
        state.newVehicleFuelAmount = Math.max(state.newVehicleFuelAmount - action.payload, 0);
      }
    },
    reset: (state) => {
      state.vehicle = null;
      state.station = null;
      state.selectedFuelAmount = 0;
      state.selectedFuelType = null;
      state.selectedFuelGrade = null;
      state.selectedPaymentType = PaymentType.Card;
      state.refuelingTime = null;
    },
  },
});

export const vehicleSelector = (state: RootState) => state.gasStation.vehicle;
export const stationSelector = (state: RootState) => state.gasStation.station;

export const selectedFuelTypeSelector = (state: RootState) => state.gasStation.selectedFuelType;
export const selectedFuelGradeSelector = (state: RootState) => state.gasStation.selectedFuelGrade;
export const selectedFuelAmountSelector = (state: RootState) => state.gasStation.selectedFuelAmount;
export const selectedProductIdSelector = (state: RootState) => state.gasStation.selectedProductId;
export const selectedPaymentTypeSelector = (state: RootState) =>
  state.gasStation.selectedPaymentType;

export const refuelingTimeSelector = (state: RootState) => state.gasStation.refuelingTime;
export const newVehicleAmountSelector = (state: RootState) => state.gasStation.newVehicleFuelAmount;
export const vehicleFuelAmountSelector = (state: RootState) => state.gasStation.vehicle.fuelAmount;

export const gasStationReducer = gasStationSlice.reducer;
export const {
  setSelectedFuelType,
  setSelectedFuelGrade,
  setSelectedFuelAmount,
  setSelectedProductId,
  setStation,
  setVehicle,
  setSelectedPaymentType,
  setRefuelingTime,
  setVehicleFuelAmount,
  setNewVehicleFuelAmount,
  reset,
  incrementVehicleFuelAmount,
  decrementNewVehicleFuelAmount,
} = gasStationSlice.actions;
